/**
 * 销售查询【批量打印】split-button 载荷组装（纯函数，零重依赖）。
 *
 * 从 salesQueryTreeGrid.vue 的旧 salesQueryPrint_clickHandler 抽取的参数组装逻辑：
 * - 直点主按钮（D2）：仅打印勾选的第一条，JOB_UUID 为单元素数组（与旧按钮勾 1 条同形）；
 *   不传 printSource——单打印的文件历史靠 ctx.uuid（cloudMetaData）落库，开闸门反而同单双录。
 * - 下拉【按业务编号打印】（D3）：JOB_UUID 单值占位 + multiplePrint + multipleJobUuid 全量，
 *   由报表端按 multipleJobUuid 逐单拆分（≥2 份打 zip、1 份单文件，均为后端原生能力）。
 * - Rework R2（文件历史）：后端逐单落「文件历史」的唯一闸门是 printSource ∈
 *   {1,2,3,4,5,13,6}（report-service ReportViewServiceImpl.uploadFileHistory 及节点服务
 *   IndexController 同一套门卫），参照页（bookingTreeGrid 系）均传 per-LOB BATCH_PRINT_*.code。
 *   故下拉分支按首行 lob 映射注入 printSource（未映射时用 defaultPrintSource 保底），
 *   并改用空 Job 实体作 ctx——否则 V3 cloudMetaData.uuid 取首单 uuid，N 份导出原件
 *   会全部挂到首单文件历史（人验 R2 现象），逐单记录反而双录。
 * - 不传 LOB/poItemsTabViewModel（D4）：销售查询为跨业务类型列表，海运订舱
 *   参照页的 SE 专属参数不适用。
 * - 业务类型一致性校验（D7）：搬移自旧 checkJobTypeHasDifferent，开关由调用方注入。
 *
 * 重依赖（Job 构造、i18n label、config 开关、printSource 常量表）由调用方注入，保持模块可独立单测。
 */

/**
 * 校验勾选行业务类型（lob）是否一致（搬移自组件内旧 checkJobTypeHasDifferent）。
 * @param {Array} rows 勾选行
 * @returns {boolean} true = 存在不一致
 */
function hasDifferentJobType(rows) {
  if (!rows || rows.length <= 0) return false
  let type = ''
  for (const job of rows) {
    if (!type) type = job.lob
    if (type !== job.lob) return true
  }
  return false
}

/**
 * 组装销售查询批量打印模板弹窗载荷。
 * @param {Array}    rows                              已勾选行（含 uuid/lob）
 * @param {string}   command                           触发命令：主按钮直点 $t('SeaExport.BatchPrint')
 *                                                        或下拉 $t('queryJob.PrintByBusinessNumber')
 * @param {Object}   opts
 * @param {string}   opts.printByBusinessNumberLabel   this.$t('queryJob.PrintByBusinessNumber')
 * @param {boolean}  [opts.jobTypeDiffCheckEnabled]    this.checkJobTypeHasDiff（config 项）
 * @param {Function} opts.buildCtx                     row => new Job(row).fillDefaultValue().getData()
 * @param {Function} [opts.buildEmptyCtx]              () => new Job().fillDefaultValue().getData()
 *                                                        （R2：下拉分支用空实体，防原件落首单；未注入时回退 buildCtx(rows[0])）
 * @param {Object}   [opts.printSourceByLob]           lob => BATCH_PRINT_*.code 映射（R2 文件历史闸门）
 * @param {*}        [opts.defaultPrintSource]         lob 未映射时的保底 code
 * @returns {{blocked:'JOB_TYPE_DIFF'}|{blocked:'NO_SELECTION'}|
 *           {ctx:*, category:string, data:Object, jobUuidList:string[]}}
 */
export function buildBatchPrintDialogPayload(rows, command, opts) {
  if (!rows || rows.length === 0) {
    return { blocked: 'NO_SELECTION' }
  }
  if (opts.jobTypeDiffCheckEnabled && hasDifferentJobType(rows)) {
    return { blocked: 'JOB_TYPE_DIFF' }
  }
  const jobKeys = Array.from(rows, p => p.uuid)
  const isByBusinessNumber = command === opts.printByBusinessNumberLabel
  const data = {}
  if (isByBusinessNumber) {
    // 按业务编号打印：单值占位 + 全量 multipleJobUuid，报表端逐单拆分
    data.JOB_UUID = jobKeys[0]
    data.multiplePrint = true
    data.multipleJobUuid = jobKeys
    // R2：后端 uploadFileHistory 仅在 printSource ∈ {1,2,3,4,5,13,6} 时逐单落文件历史
    const printSource = (opts.printSourceByLob && opts.printSourceByLob[rows[0].lob]) || opts.defaultPrintSource
    if (printSource) {
      data.printSource = printSource
    }
  } else {
    // 直点：仅打印勾选的第一条（单元素数组，与旧按钮勾 1 条形态同形）
    data.JOB_UUID = [jobKeys[0]]
  }
  // R2：下拉分支用空实体作 ctx（cloudMetaData.uuid 为空/随机，导出原件不落首单文件历史）
  const ctx = (isByBusinessNumber && opts.buildEmptyCtx) ? opts.buildEmptyCtx() : opts.buildCtx(rows[0])
  return {
    ctx,
    // 模板类型 SALETYPE：销售查询专用打印模板类型（弹窗按此 templateType 查模板列表）
    category: 'SALETYPE',
    data,
    jobUuidList: jobKeys
  }
}
